"use server";

import { auth } from "@/auth";
import { prisma } from "@/lib/prisma";

export async function getOrCreateCart(userId: string) {
  let cart = await prisma.cart.findUnique({
    where: { userId },
    include: {
      items: {
        orderBy: { createdAt: "asc" },
        include: {
          product: {
            include: {
              images: {
                orderBy: { sortOrder: "asc" },
                take: 1,
              },
              category: true,
            },
          },
        },
      },
    },
  });

  if (!cart) {
    cart = await prisma.cart.create({
      data: { userId },
      include: {
        items: {
          orderBy: { createdAt: "asc" },
          include: {
            product: {
              include: {
                images: {
                  orderBy: { sortOrder: "asc" },
                  take: 1,
                },
                category: true,
              },
            },
          },
        },
      },
    });
  }

  return cart;
}

export async function getServerCart() {
  const session = await auth();
  if (!session?.user?.id) return null;

  return getOrCreateCart(session.user.id);
}

export async function addToServerCart(productId: string, quantity = 1) {
  const session = await auth();
  if (!session?.user?.id) {
    throw new Error("Non autorisé");
  }

  if (!productId || quantity <= 0) {
    throw new Error("Données panier invalides");
  }

  const product = await prisma.product.findUnique({
    where: { id: productId },
    select: {
      id: true,
      isPublished: true,
      stock: true,
    },
  });

  if (!product || !product.isPublished) {
    throw new Error("Produit introuvable");
  }

  const cart = await prisma.cart.upsert({
    where: { userId: session.user.id },
    update: {},
    create: { userId: session.user.id },
  });

  const existing = await prisma.cartItem.findUnique({
    where: {
      cartId_productId: {
        cartId: cart.id,
        productId,
      },
    },
  });

  const nextQuantity = (existing?.quantity ?? 0) + quantity;

  if (nextQuantity > product.stock) {
    throw new Error("Stock insuffisant");
  }

  if (existing) {
    await prisma.cartItem.update({
      where: { id: existing.id },
      data: { quantity: nextQuantity },
    });
  } else {
    await prisma.cartItem.create({
      data: {
        cartId: cart.id,
        productId,
        quantity,
      },
    });
  }

  return getOrCreateCart(session.user.id);
}

export async function updateServerCartItem(productId: string, quantity: number) {
  const session = await auth();
  if (!session?.user?.id) {
    throw new Error("Non autorisé");
  }

  const cart = await prisma.cart.findUnique({
    where: { userId: session.user.id },
  });

  if (!cart) return null;

  if (quantity <= 0) {
    await prisma.cartItem.deleteMany({
      where: {
        cartId: cart.id,
        productId,
      },
    });

    return getOrCreateCart(session.user.id);
  }

  const product = await prisma.product.findUnique({
    where: { id: productId },
    select: {
      stock: true,
      isPublished: true,
    },
  });

  if (!product || !product.isPublished) {
    throw new Error("Produit introuvable");
  }

  if (quantity > product.stock) {
    throw new Error("Stock insuffisant");
  }

  await prisma.cartItem.updateMany({
    where: {
      cartId: cart.id,
      productId,
    },
    data: { quantity },
  });

  return getOrCreateCart(session.user.id);
}

export async function removeFromServerCart(productId: string) {
  const session = await auth();
  if (!session?.user?.id) {
    throw new Error("Non autorisé");
  }

  const cart = await prisma.cart.findUnique({
    where: { userId: session.user.id },
  });

  if (!cart) return null;

  await prisma.cartItem.deleteMany({
    where: {
      cartId: cart.id,
      productId,
    },
  });

  return getOrCreateCart(session.user.id);
}

export async function clearServerCart() {
  const session = await auth();
  if (!session?.user?.id) {
    throw new Error("Non autorisé");
  }

  const cart = await prisma.cart.findUnique({
    where: { userId: session.user.id },
  });

  if (!cart) return null;

  await prisma.cartItem.deleteMany({
    where: { cartId: cart.id },
  });

  return getOrCreateCart(session.user.id);
}